import { useMemo, useState } from 'react'
import { ChevronDown, Mail } from 'lucide-react'
import { Reveal } from '@/components/landing/Reveal'
import { PageOutro, VisionSection } from '@/components/landing/Section'
import { useDocumentHead } from '@/hooks/useDocumentHead'
import { FAQ_CATEGORIES, FAQ_ITEMS, type FaqItem } from '@/content/faq'

/** The FAQ page: category filter, grouped Q&A, and a way to reach a human. */
export function FaqPage() {
  const jsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.a,
        },
      })),
    }),
    [],
  )

  useDocumentHead({
    title: 'NevTan AI FAQ | Local AI Models, Hardware, Privacy',
    description:
      "What NevTan AI costs, which models it runs today, how it picks the right one for your hardware, and what's still being built.",
    canonical: 'https://ai.nevtan.com/faq',
    jsonLd,
  })

  return (
    <>
      <Faq />
      <PageOutro>
        <h2 className="nv-h2 text-[1.6rem] sm:text-[2rem]">Still have questions?</h2>
        <p className="mx-auto mt-3 max-w-xl text-[15px] leading-relaxed text-brand-body">
          If your question isn&rsquo;t answered here, the NevTan team is ready to help.
        </p>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          <a href="https://nevtan.com/contact-us" target="_blank" rel="noreferrer" className="nv-btn-primary-lg">
            <Mail size={16} /> Contact us
          </a>
        </div>
      </PageOutro>
    </>
  )
}

/**
 * Which item is open within each visible category group.
 *
 * <p>Kept per category rather than one global id: with "All" selected there
 * are several groups on screen at once, and each behaves as its own
 * single-open accordion rather than fighting the others for the one open
 * slot.
 */
function useOpenItemPerGroup(groups: { category: string; items: FaqItem[] }[]) {
  const [openByCategory, setOpenByCategory] = useState<Record<string, string>>(() =>
    defaultOpenMap(groups),
  )

  const toggle = (category: string, id: string) => {
    setOpenByCategory((prev) => ({
      ...prev,
      [category]: prev[category] === id ? '' : id,
    }))
  }

  const isOpen = (category: string, id: string) => openByCategory[category] === id

  return { isOpen, toggle, resetTo: (next: { category: string; items: FaqItem[] }[]) => setOpenByCategory(defaultOpenMap(next)) }
}

function defaultOpenMap(groups: { category: string; items: FaqItem[] }[]): Record<string, string> {
  const defaults: Record<string, string> = {}
  groups.forEach((group) => {
    if (group.items[0]) defaults[group.category] = group.items[0].id
  })
  return defaults
}

function Faq() {
  const [category, setCategory] = useState<(typeof FAQ_CATEGORIES)[number]>('All')

  // Grouped by category, in FAQ_CATEGORIES order, so the page reads as
  // distinct sections instead of one flat list. Picking a category chip
  // narrows this down to that one section.
  const groupedFaqs = useMemo(() => {
    const activeCategories = category === 'All' ? FAQ_CATEGORIES.filter((c) => c !== 'All') : [category]
    return activeCategories
      .map((cat) => ({ category: cat, items: FAQ_ITEMS.filter((item) => item.category === cat) }))
      .filter((group) => group.items.length > 0)
  }, [category])

  const { isOpen, toggle, resetTo } = useOpenItemPerGroup(groupedFaqs)

  const selectCategory = (cat: (typeof FAQ_CATEGORIES)[number]) => {
    setCategory(cat)
    const activeCategories = cat === 'All' ? FAQ_CATEGORIES.filter((c) => c !== 'All') : [cat]
    resetTo(
      activeCategories
        .map((c) => ({ category: c, items: FAQ_ITEMS.filter((item) => item.category === c) }))
        .filter((group) => group.items.length > 0),
    )
  }

  return (
    <VisionSection
      id="faq"
      eyebrow="Frequently asked questions"
      title="Questions you might have"
      lede="Browse answers about NevTan AI, grouped by topic — what it costs, which models it runs today, and what's still being built."
    >
      <div className="flex flex-wrap gap-2">
        {FAQ_CATEGORIES.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => selectCategory(cat)}
            className={`rounded-lg px-3 py-1.5 text-[13px] font-bold transition ${
              category === cat
                ? 'bg-brand text-white shadow-brand'
                : 'border border-brand-line bg-brand-paper text-brand-body hover:border-brand hover:text-brand'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      <div className="mt-8 flex flex-col gap-10">
        {groupedFaqs.map((group, groupIndex) => (
          <Reveal key={group.category} delay={groupIndex * 60}>
            <div>
              <h3 className="text-[15px] font-extrabold text-brand-heading">{group.category}</h3>
              <div className="mt-3 flex flex-col gap-2.5">
                {group.items.map((item) => (
                  <FaqAccordionItem
                    key={item.id}
                    item={item}
                    open={isOpen(group.category, item.id)}
                    onToggle={() => toggle(group.category, item.id)}
                  />
                ))}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </VisionSection>
  )
}

function FaqAccordionItem({ item, open, onToggle }: { item: FaqItem; open: boolean; onToggle: () => void }) {
  return (
    <div
      className={`overflow-hidden rounded-xl border transition ${
        open ? 'border-brand/40 bg-brand-tint/20 shadow-card' : 'border-brand-line bg-brand-paper'
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left"
      >
        <span
          className={`text-[14px] font-bold leading-snug sm:text-[15px] ${
            open ? 'text-brand' : 'text-brand-heading'
          }`}
        >
          {item.q}
        </span>
        <ChevronDown
          size={18}
          className={`shrink-0 text-brand-muted transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="px-4 pb-4 text-[13.5px] leading-relaxed text-brand-body">{item.a}</div>
      )}
    </div>
  )
}
